'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { IoStarSharp } from 'react-icons/io5';
import { useViewportMotion } from './use-viewport-motion';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import type { Reference } from '@eliadmin/core/src/api/references/references-types';

const ROTATE_MS = 20000;

export function References({
  subtitle,
  title,
  references,
}: {
  subtitle: string;
  title: string;
  references: Reference[];
}) {
  const [activeId, setActiveId] = useState<number | undefined>(references[0]?.id);
  const { ref, active } = useViewportMotion();
  const [isHovered, setIsHovered] = useState(false);
  const [direction, setDirection] = useState<1 | -1>(1);

  const activeIndex = useMemo(
    () =>
      Math.max(
        0,
        references.findIndex(x => x.id === activeId),
      ),
    [activeId, references],
  );

  const activeItem = references[activeIndex];

  const goToIndex = (nextIndex: number) => {
    const clamped = ((nextIndex % references.length) + references.length) % references.length;
    setDirection(clamped > activeIndex ? 1 : -1);
    setActiveId(references[clamped].id);
  };

  useEffect(() => {
    if (isHovered || !active || references.length < 2) return;

    const t = window.setInterval(() => {
      goToIndex(activeIndex + 1);
    }, ROTATE_MS);

    return () => window.clearInterval(t);
  }, [isHovered, active, activeIndex, references]);

  const activeReferenceClassName = 'bg-black w-4';
  const inactiveReferenceClassName = 'bg-[#EDEDED] w-2';

  const cardVariants = {
    enter: (dir: 1 | -1) => ({
      opacity: 0,
      x: dir * 18,
      filter: 'blur(1px)',
    }),
    center: {
      opacity: 1,
      x: 0,
      filter: 'blur(0px)',
      transition: { duration: 0.35, ease: 'easeOut' },
    },
    exit: (dir: 1 | -1) => ({
      opacity: 0,
      x: dir * -18,
      filter: 'blur(1px)',
      transition: { duration: 0.25, ease: 'easeIn' },
    }),
  } as const;

  if (!activeItem) return null;

  return (
    <section
      ref={ref}
      className={cn('custom-section', 'pt-16 sm:pt-12 lg:pt-16', 'ea-section-medium__bottom')}
    >
      <div
        className={cn(
          'w-full max-w-[932px]',
          'flex flex-col items-center justify-center gap-12 sm:gap-14 lg:gap-16',
        )}
      >
        <Heading
          subtitle={subtitle}
          title={title}
          className={{ container: 'items-center justify-center text-center' }}
        />

        <div
          className={cn(
            'flex w-full max-w-[996px] flex-col items-start justify-start gap-6 rounded-[24px] border-2 border-[#EDEDED] bg-white p-6 md:flex-row md:items-stretch md:justify-between lg:gap-8 lg:p-8',
            'ea-reference-card__wrapper',
          )}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex w-full flex-col items-center justify-center gap-4 rounded-[24px] bg-[#FAFAFA] p-6 sm:max-w-[282px] sm:gap-6 md:min-h-[400px] lg:gap-8 lg:p-8">
            <div className="flex items-center justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <IoStarSharp key={i} className="text-[#EDB000]" size={24} />
              ))}
            </div>

            <AnimatePresence initial={false} mode="wait" custom={direction}>
              <motion.div
                key={activeItem.id}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="flex w-full flex-col items-center justify-center gap-4"
              >
                <Image
                  src={activeItem.image.url}
                  alt={resolveImageAlt(activeItem?.image, activeItem?.name)}
                  width={128}
                  height={128}
                  className="aspect-square w-16 overflow-hidden rounded-full object-cover object-center sm:w-20 md:w-24 lg:w-32"
                  sizes="(min-width: 1024px) 128px, (min-width: 768px) 96px, (min-width: 640px) 80px, 64px"
                />
                <div className="flex w-full flex-col items-center justify-center gap-2 sm:gap-3 lg:gap-4">
                  <Heading
                    title={activeItem.name}
                    tag="h5"
                    className={{ container: 'w-full items-center justify-center text-center' }}
                  />
                  <Heading
                    title={activeItem.city}
                    tag="h6"
                    className={{
                      container: 'w-full items-center justify-center text-center',
                      title: 'text-[#969696]',
                    }}
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex w-full flex-col items-start justify-between gap-6">
            <div className="hidden h-[29px] w-full bg-transparent md:block" />

            <AnimatePresence initial={false} mode="wait" custom={direction}>
              <motion.div
                key={activeItem.id}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className="w-full"
              >
                <Paragraph innerHtml={activeItem.description} color="muted-foreground" />
              </motion.div>
            </AnimatePresence>

            <div className="flex w-full items-center justify-between">
              <div className="flex items-center justify-center gap-2">
                {references.map((item, idx) => {
                  const isActive = idx === activeIndex;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      aria-label={`Show reference ${idx + 1}`}
                      onClick={() => goToIndex(idx)}
                      className="rounded-full"
                    >
                      <div
                        className={cn(
                          'h-2 rounded-full transition-all duration-300 ease-out',
                          isActive ? activeReferenceClassName : inactiveReferenceClassName,
                        )}
                      />
                    </button>
                  );
                })}
              </div>
              <Heading title={String(activeIndex + 1).padStart(2, '0')} tag="h5" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
